import { useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLocation } from "react-router-dom";

interface NotificationPayload {
  title: string;
  body: string;
  icon?: string;
  tag?: string;
}

export const useChatNotifications = (userId: string | undefined) => {
  const location = useLocation();
  const permissionRef = useRef<NotificationPermission>("default");

  // Check if user is currently on the chat page
  const isOnChatPage = location.pathname === "/chat";

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!("Notification" in window)) {
      console.log("This browser does not support notifications");
      return false;
    }

    if (Notification.permission === "granted") {
      permissionRef.current = "granted";
      return true;
    }

    if (Notification.permission !== "denied") {
      const permission = await Notification.requestPermission();
      permissionRef.current = permission;
      return permission === "granted";
    }

    return false;
  }, []);

  // Show notification
  const showNotification = useCallback(({ title, body, icon, tag }: NotificationPayload) => {
    if (permissionRef.current !== "granted") return;
    if (document.visibilityState === "visible" && isOnChatPage) return;

    try {
      const notification = new Notification(title, {
        body,
        icon: icon || "/favicon.ico",
        tag: tag || "chat-message",
        badge: "/favicon.ico",
        requireInteraction: false,
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      // Auto-close after 5 seconds
      setTimeout(() => notification.close(), 5000);
    } catch (error) {
      console.error("Error showing notification:", error);
    }
  }, [isOnChatPage]);

  // Fetch sender name from public profiles
  const fetchSenderName = useCallback(async (senderId: string): Promise<string> => {
    const { data } = await supabase
      .from("public_profiles")
      .select("full_name")
      .eq("user_id", senderId)
      .single();

    return data?.full_name || "Someone";
  }, []);

  // Subscribe to direct messages
  useEffect(() => {
    if (!userId) return;

    // Request permission on mount
    requestPermission();

    const dmChannel = supabase
      .channel(`dm-notifications-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "direct_messages",
          filter: `receiver_id=eq.${userId}`,
        },
        async (payload) => {
          const message = payload.new as {
            sender_id: string;
            content: string;
            id: string;
          };

          // Don't notify for own messages
          if (message.sender_id === userId) return;

          const senderName = await fetchSenderName(message.sender_id);
          
          showNotification({
            title: `New message from ${senderName}`,
            body: message.content.substring(0, 100),
            tag: `dm-${message.id}`,
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(dmChannel);
    };
  }, [userId, requestPermission, showNotification, fetchSenderName]);

  // Subscribe to group chat messages
  useEffect(() => {
    if (!userId) return;

    // First get user's group memberships
    const setupGroupSubscription = async () => {
      const { data: memberships } = await supabase
        .from("group_chat_members")
        .select("group_id")
        .eq("user_id", userId);

      if (!memberships || memberships.length === 0) return null;

      const groupIds = memberships.map((m) => m.group_id);

      const groupChannel = supabase
        .channel(`group-notifications-${userId}`)
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "group_chat_messages",
          },
          async (payload) => {
            const message = payload.new as {
              sender_id: string;
              group_id: string;
              content: string;
              id: string;
            };

            // Check if message is for a group user belongs to
            if (!groupIds.includes(message.group_id)) return;

            // Don't notify for own messages
            if (message.sender_id === userId) return;

            // Get group name and sender name
            const [senderName, groupData] = await Promise.all([
              fetchSenderName(message.sender_id),
              supabase
                .from("group_chats")
                .select("name")
                .eq("id", message.group_id)
                .single(),
            ]);

            const groupName = groupData.data?.name || "Group";

            showNotification({
              title: `${senderName} in ${groupName}`,
              body: message.content.substring(0, 100),
              tag: `group-${message.id}`,
            });
          }
        )
        .subscribe();

      return groupChannel;
    };

    let groupChannel: ReturnType<typeof supabase.channel> | null = null;
    
    setupGroupSubscription().then((channel) => {
      groupChannel = channel;
    });

    return () => {
      if (groupChannel) {
        supabase.removeChannel(groupChannel);
      }
    };
  }, [userId, showNotification, fetchSenderName]);

  return { requestPermission };
};
